import React from 'react';
import { Link } from 'react-router-dom';
import SeoMeta from '../components/SeoMeta';
import ArticleLayout from '../components/articles/ArticleLayout';
import { Callout, Checks, Points, DataTable, Timeline } from '../components/articles/blocks';
import { FlowDiagram } from '../components/common/Diagram';

const DigitalizacionTransportePiezaPage = () => {
  return (
    <>
      <SeoMeta
        title="Digitalización del Documento de Control: del PDF obligatorio a datos eFTI | BlueCMR"
        description="Calendario de la digitalización del documento de control en España: del PDF obligatorio a los datos estructurados eFTI, con la reforma del ROTT."
        canonical="https://bluecmr.com/articulos/digitalizacion-documento-control-transporte-datos-efti"
        type="article"
        imageWidth={1402}
        imageHeight={723}
        keywords="digitalización documento de control, documento control digital transporte, ROTT modificación, eFTI datos estructurados, plataforma eFTI, Ley Movilidad Sostenible 2026, PDF obligatorio transporte, documento control electrónico, BlueCMR"
        breadcrumbs={[
          { name: 'Inicio', url: 'https://bluecmr.com/' },
          { name: 'Artículos', url: 'https://bluecmr.com/articulos' },
          { name: 'Digitalización del Documento de Control de Transporte', url: 'https://bluecmr.com/articulos/digitalizacion-documento-control-transporte-datos-efti' }
        ]}
        structuredData={[
          {
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            headline: 'Digitalización del DeCA: del PDF obligatorio a datos eFTI',
            description: 'Cronograma completo de la digitalización del Documento de Control de transporte en España: PDF obligatorio en 2026, datos estructurados eFTI con la modificación del ROTT.',
            image: 'https://www.bluecmr.com/og/articulo-efti.jpg',
            datePublished: '2026-02-25',
            dateModified: '2026-11-04',
            author: { '@type': 'Organization', name: 'BlueCMR', url: 'https://bluecmr.com' },
            publisher: { '@type': 'Organization', name: 'BlueCMR', logo: { '@type': 'ImageObject', url: 'https://www.bluecmr.com/og/logo-bluecmr.png' } },
            mainEntityOfPage: { '@type': 'WebPage', '@id': 'https://bluecmr.com/articulos/digitalizacion-documento-control-transporte-datos-efti' },
            articleSection: 'Normativa',
            keywords: 'digitalización documento de control, documento control digital transporte, ROTT modificación, eFTI datos estructurados, plataforma eFTI, Ley Movilidad Sostenible 2026'
          }
        ]}
      />
      <ArticleLayout
        url="/articulos/digitalizacion-documento-control-transporte-datos-efti"
        title="Digitalización del documento de control: del PDF a los datos eFTI"
        deck="Del papel al PDF, del PDF a los datos. La digitalización del DeCA en el transporte de mercancías no termina con el PDF obligatorio. La modificación del ROTT marca el camino hacia los datos estructurados eFTI."
        updated="2026-11-04"
        readingMinutes={5}
        toc={[
          { id: 'efti', label: 'Qué es eFTI' },
          { id: 'cronograma', label: 'Cronograma de la digitalización' },
          { id: 'riesgo', label: 'El riesgo de quedar obsoleto' },
          { id: 'bluecmr', label: 'BlueCMR: datos eFTI desde el primer día' },
          { id: 'fuentes', label: 'Fuentes y normativa' },
        ]}
        closing={{ title: 'Prepárese hoy para el transporte digital de mañana', text: 'BlueCMR almacena sus datos en formato eFTI desde el primer documento. Cumple con 2026 y estará listo para la modificación del ROTT sin cambiar de plataforma.' }}
      >
        <p>
          El DeCA —el documento de control administrativo regulado por la Orden FOM/2861/2012— es la documentación obligatoria que acompaña a las mercancías en el transporte por carretera en España. Su digitalización sigue una hoja de ruta con varios hitos que transformarán la forma en que el sector gestiona y presenta esta documentación.
        </p>

        <h2 id="efti">Qué es eFTI</h2>
        <p>
          El <strong>Reglamento (UE) 2020/1056 sobre información electrónica relativa al transporte de mercancías (eFTI)</strong> establece un marco común para que las empresas compartan con las autoridades la información del transporte en formato electrónico. Abarca todos los modos: carretera, ferrocarril, vías navegables interiores y aire.
        </p>
        <p>
          <strong>No impone nuevos contenidos</strong>: exige que las autoridades acepten esa información, de forma estándar e interoperable en toda la Unión, cuando llegue a través de plataformas eFTI certificadas.
        </p>
        <FlowDiagram
          label="Arquitectura eFTI"
          steps={[
            { title: 'Empresas', text: 'Cargador, transportista y destinatario generan y firman sus documentos.' },
            { title: 'Plataforma eFTI', text: 'Guarda los datos según el conjunto común de datos de eFTI. Debe estar certificada.', strong: true },
            { title: 'eFTI Gate', text: 'Pasarela nacional de cada Estado; las Gates están conectadas entre sí.' },
            { title: 'Autoridades', text: 'Consultan los datos en una inspección, sin papel ni PDF.' },
          ]}
          caption="Arquitectura eFTI: las autoridades acceden a los datos del transporte a través de las Gates. Desde julio de 2027 deben aceptarlos cuando lleguen por plataformas certificadas."
        />
        <Callout title="Certificación de plataformas">
          <p>A noviembre de 2026, el proceso de certificación de plataformas eFTI todavía no se ha abierto; está previsto para los próximos meses. BlueCMR trabaja ya con el estándar eFTI y se certificará cuando se abra.</p>
        </Callout>

        <h2 id="cronograma">Cronograma de la digitalización del DeCA</h2>
        <p>Cada hito acerca al sector a un modelo donde lo importante no es el documento, sino los datos.</p>
        <Timeline
          items={[
            {
              date: 'Mayo 2023 → Junio 2026',
              title: 'Completado',
              text: <>Resolución de 22 de mayo de 2023 de la <strong>Dirección General de Transporte Terrestre</strong>, que establece los primeros requisitos para documentos electrónicos (PDF/A con código QR). <strong>Sustituida en junio 2026</strong> por la Resolución de 5/06/2026, que simplifica el formato a PDF nativo digital (≤5 MB) con QR y URL HTTPS.</>,
            },
            {
              date: 'Diciembre 2025',
              title: 'Completado',
              text: <>Publicación de la <strong>Ley de Movilidad Sostenible</strong>, que establece que el DeCA es obligatorio en formato digital. La obligatoriedad entra en vigor a los 10 meses de su publicación.</>,
            },
            {
              date: 'Octubre 2026',
              title: 'En vigor: DeCA obligatoriamente digital',
              text: (
                <>
                  La <strong>Ley 9/2025 de Movilidad Sostenible</strong> exige que el DeCA se emita exclusivamente en formato digital: <strong>PDF nativo digital (≤5 MB) con código QR</strong>, conforme a la Resolución de 5 de junio de 2026 de la Dirección General de Transporte por Carretera y Ferrocarril.
                  <span className="mt-2 block"><strong>Fin del papel.</strong> Desde el 5 de octubre de 2026 las empresas que no estén preparadas se enfrentan a incidencias en inspecciones y posibles sanciones.</span>
                </>
              ),
            },
            {
              date: 'Pendiente',
              title: 'Publicación de la modificación del ROTT: del PDF a los datos estructurados eFTI',
              text: (
                <>
                  La modificación del art. 222 y ss. del RD 1211/1990 establece que el DeCA <strong>transitará desde el formato PDF + QR hacia la comunicación de datos electrónicos</strong> basados en el modelo de datos eFTI.
                  <span className="mt-2 block"><strong>La pieza que faltaba.</strong> Este es el cambio conceptual clave: lo importante deja de ser el fichero PDF y pasa a ser la <strong>transmisión de datos estructurados</strong> a las autoridades. Los detalles técnicos se establecerán por Resolución de la Dirección General de Transporte por Carretera y Ferrocarril.</span>
                </>
              ),
            },
            {
              date: 'Julio 2027',
              title: 'Las autoridades europeas obligadas a aceptar datos eFTI',
              text: (
                <>
                  El <strong>Reglamento eFTI</strong> obliga a las autoridades de todos los Estados miembros a aceptar datos transmitidos por plataformas eFTI certificadas. Los operadores no están obligados a presentarlos en este formato, pero las autoridades no podrán rechazarlos.
                  <span className="mt-2 block">La arquitectura eFTI conecta plataformas privadas (eFTI Platforms) con pasarelas nacionales (eFTI Gates) interconectadas entre sí.</span>
                </>
              ),
            },
            {
              date: 'Fecha pendiente',
              title: 'Entrada en vigor de la modificación del ROTT',
              text: <>El DeCA de transporte interior pasará <strong>obligatoriamente</strong> del formato PDF + QR a la transmisión de datos estructurados eFTI. Las plataformas deberán estar certificadas conforme a las reglas de la Comisión Europea.</>,
            },
            {
              date: '2029',
              title: 'Evaluación sobre la obligatoriedad de eFTI a nivel europeo',
              text: <>La Comisión Europea evaluará si debe <strong>exigirse a los operadores</strong> la presentación de documentos en formato eFTI, pasando de potestativo a obligatorio en toda la UE.</>,
            },
          ]}
        />
        <DataTable
          head={['Etapa', 'Papel', 'PDF', 'Datos eFTI']}
          rows={[
            ['Hasta octubre 2026', 'Permitido', 'Permitido', 'No disponible'],
            ['Desde octubre 2026 — Ley de Movilidad Sostenible', 'No cumple', 'Obligatorio', 'No disponible'],
            ['Desde julio 2027 — Reglamento eFTI', '—', 'Obligatorio', 'Permitido'],
            ['Entrada en vigor de la modificación del ROTT', '—', 'No cumple', 'Obligatorio'],
          ]}
        />

        <h2 id="riesgo">El riesgo de anclarse a una solución que puede quedar obsoleta</h2>
        <p>
          La solución que se elija hoy para cumplir con la obligatoriedad de octubre de 2026 debe estar preparada para el modelo eFTI, o habrá que cambiar de sistema cuando se apruebe la modificación del ROTT, prevista entre 2027 y 2029.
        </p>
        <Points
          cols={3}
          items={[
            { title: 'Solo PDF', text: <>Herramientas que se limitan a generar un PDF firmado con QR cumplen con 2026, pero <strong>no podrán transmitir datos estructurados</strong> a las eFTI Gates.</> },
            { title: 'Desarrollo propio', text: <>Empresas que desarrollen su propio sistema tendrán que <strong>invertir de nuevo</strong> para adaptarlo al modelo eFTI, la conectividad con las Gates y la certificación.</> },
            { title: 'Plataforma eFTI', text: <>Plataformas que ya almacenan datos en formato eFTI ofrecen una <strong>transición sin fricciones</strong>: cumplen con 2026 hoy y estarán listas sin cambios.</> },
          ]}
        />

        <h2 id="bluecmr">BlueCMR: datos eFTI desde el primer día</h2>
        <p>
          BlueCMR no es una solución que genera PDFs y los firma. Es una plataforma de documentación digital de transporte que almacena todos los datos en formato estructurado conforme al modelo eFTI desde el momento en que se crea el documento.
        </p>
        <h3>Obligatoriedad 2026</h3>
        <Checks
          items={[
            <><strong>PDF nativo digital</strong> conforme a la Resolución de junio 2026</>,
            <><strong>Código QR integrado</strong> con acceso HTTPS</>,
            <><strong>Firma electrónica avanzada</strong> conforme a eIDAS cuando el DeCA tiene finalidad contractual</>,
            <><strong>Todos los campos obligatorios</strong> de la Orden FOM/2861/2012</>,
          ]}
        />
        <h3>Preparado para la modificación del ROTT</h3>
        <Checks
          items={[
            <><strong>Datos en formato eFTI</strong> — modelo Common Data Set europeo</>,
            <><strong>Subconjuntos nacionales</strong> de España ya mapeados</>,
            <><strong>API REST documentada</strong> para el nuevo modelo de datos</>,
            <><strong>Preparado</strong> para los futuros procesos de certificación eFTI</>,
          ]}
        />
        <Callout>
          <p><strong>Trabajar con BlueCMR desde hoy significa que cuando la modificación del ROTT entre en vigor, no habrá que cambiar de plataforma, migrar datos ni adaptar procesos.</strong> Los datos ya están en el formato correcto.</p>
        </Callout>
        <p>
          Artículos relacionados:{' '}
          <Link to="/documentos/documento-control">DeCA digital</Link>
          {' · '}
          <Link to="/articulos/formato-pdfa1a-documento-control-digital-2026">Requisitos técnicos del DeCA digital 2026</Link>
          {' · '}
          <Link to="/articulos/diferencias-entre-dca-carta-de-porte-y-cmr-ecmr">Diferencias entre DeCA, Carta de Porte y CMR/eCMR</Link>
        </p>

        <h2 id="fuentes">Fuentes y normativa de referencia</h2>
        <ul>
          <li><a href="https://www.boe.es/buscar/act.php?id=BOE-A-2025-24545" target="_blank" rel="noopener noreferrer">Ley 9/2025, de Movilidad Sostenible (BOE)</a></li>
          <li><a href="https://www.boe.es/eli/es/o/2012/12/13/fom2861" target="_blank" rel="noopener noreferrer">Orden FOM/2861/2012 — Documento de Control</a></li>
          <li><a href="https://www.boe.es/buscar/doc.php?id=BOE-A-2026-12784" target="_blank" rel="noopener noreferrer">Resolución de 5 de junio de 2026 (BOE-A-2026-12784) — Requisitos técnicos del DeCA</a></li>
          <li><a href="https://www.boe.es/eli/es/res/2023/05/22/(4)" target="_blank" rel="noopener noreferrer">Resolución de 22 de mayo de 2023</a> (derogada)</li>
          <li><a href="https://eur-lex.europa.eu/eli/reg/2020/1056/oj" target="_blank" rel="noopener noreferrer">Reglamento eFTI (UE 2020/1056)</a></li>
          <li><a href="https://eur-lex.europa.eu/eli/reg/2014/910/oj" target="_blank" rel="noopener noreferrer">Reglamento eIDAS (UE 910/2014)</a></li>
          <li><a href="https://webgate.ec.europa.eu/fpfis/wikis/spaces/eFTI/overview" target="_blank" rel="noopener noreferrer">Wiki eFTI — Comisión Europea</a></li>
        </ul>
      </ArticleLayout>
    </>
  );
};

export default DigitalizacionTransportePiezaPage;
